import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { ShieldCheck, ShieldX, Calendar, Award, User, BookOpen } from "lucide-react";

export default function VerifyCertificate() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/verify/${id}`)
      .then((r) => setData(r.data))
      .catch(() => setErr(true))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="p-16 text-center text-marino/60">Verificando...</div>;

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-marino/5 py-16 px-4">
      <div className="max-w-xl mx-auto bg-white rounded-2xl shadow-xl overflow-hidden border border-gris/30 animate-fade-in">
        <div className="bg-marino text-white px-6 py-8 text-center relative">
          <div className="absolute -right-6 -top-6 w-32 h-32 rounded-full bg-naranja/15 blur-2xl" />
          <div className="relative">
            {err ? (
              <>
                <div className="w-16 h-16 mx-auto rounded-full bg-red-500/20 border-2 border-red-400 flex items-center justify-center">
                  <ShieldX className="w-9 h-9 text-red-300" strokeWidth={1.75} />
                </div>
                <h1 className="mt-4 font-heading font-black text-2xl">Certificado no válido</h1>
                <p className="mt-2 text-white/70 text-sm">No se pudo verificar este certificado. Puede ser inexistente, revocado o el enlace es incorrecto.</p>
              </>
            ) : (
              <>
                <div className="w-16 h-16 mx-auto rounded-full bg-green-400/20 border-2 border-green-300 flex items-center justify-center">
                  <ShieldCheck className="w-9 h-9 text-green-300" strokeWidth={1.75} />
                </div>
                <h1 className="mt-4 font-heading font-black text-2xl">Certificado verificado</h1>
                <p className="mt-2 text-white/70 text-sm">Este certificado ha sido emitido oficialmente por OM-PEC.</p>
              </>
            )}
          </div>
        </div>

        {!err && data && (
          <div className="p-6 sm:p-8 space-y-4">
            <Row icon={User} label="Otorgado a" value={data.student_name} testid="verify-name" />
            <Row icon={BookOpen} label="Curso" value={data.course_title} testid="verify-course" />
            <Row icon={Award} label="Calificación" value={`${data.score}%`} testid="verify-score" />
            <Row icon={Calendar} label="Fecha de emisión" value={(data.issued_at || "").slice(0, 10)} testid="verify-date" />
            <div className="pt-4 border-t border-gris/20">
              <div className="text-xs text-marino/60">ID del certificado</div>
              <div className="mt-1 font-mono text-xs text-marino break-all" data-testid="verify-id">{data.enrollment_id}</div>
            </div>
            <div className="text-center pt-4">
              <div className="inline-flex items-center gap-2 text-xs text-marino/70 italic">
                "Protegemos personas, fortalecemos empresas."
              </div>
              <div className="text-xs text-marino/60 mt-1">— {data.issuer}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ icon: Icon, label, value, testid }) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="w-5 h-5 text-naranja mt-0.5 flex-shrink-0" strokeWidth={1.75} />
      <div className="flex-1 min-w-0">
        <div className="text-xs uppercase tracking-wider text-marino/60 font-semibold">{label}</div>
        <div className="text-marino font-medium mt-0.5 break-words" data-testid={testid}>{value}</div>
      </div>
    </div>
  );
}
